import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import RouterIcon from "@mui/icons-material/Router";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SecurityIcon from "@mui/icons-material/Security";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { getImageUrl } from "../../../utils/helpers";

const KthPatentSpecializations = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  const specializations = [
    {
      id: "ai-ml-patents",
      title: "AI & Machine Learning Patents",
      icon: (
        <SmartToyIcon sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }} />
      ),
      image: "ai-patents.png",
      description:
        "Protect your artificial intelligence and machine learning innovations with tailored patent services. Our technical expertise ensures your AI and ML patents are comprehensive and future-proof, maintaining your competitive edge in this rapidly evolving field.",
      details: [
        "Neural network architectures and training methodologies",
        "Computer vision and image recognition algorithms",
        "Natural language processing techniques",
        "Reinforcement learning systems",
        "AI-enabled hardware and specialized processors",
        "Machine learning model optimization techniques",
      ],
      linkText: "Learn More About AI Patents",
      linkPath: "/ai#ai-patent-analysis",
    },
    {
      id: "wireless-patents",
      title: "Wireless Technology Patents",
      icon: <RouterIcon sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }} />,
      image: "wireless-tech-patents.png",
      description:
        "We are leaders in wireless patent protection, leveraging our dual expertise in wireless telecommunications and patent law to provide a unique advantage. Our technical knowledge enables us to develop stronger patent applications with precise technical language that anticipates both current and future technology evolution.",
      details: [
        "Cellular technologies (5G, 6G, LTE)",
        "Wi-Fi/WLAN innovations (802.11 family)",
        "IoT/LPWAN solutions (LoRa, NB-IoT)",
        "Baseband processor logic and transceiver circuits",
        "Wireless network and infrastructure equipment",
        "RF front-end components and designs",
      ],
      linkText: "Explore Our Wireless Practice",
      linkPath: "/wireless",
    },
    {
      id: "antenna-patents",
      title: "Antenna Patents",
      icon: (
        <SignalCellularAltIcon
          sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }}
        />
      ),
      image: "antenna-phased-array.png",
      description:
        "Expert protection for antenna innovations across diverse applications. Our deep technical knowledge of electromagnetic principles and antenna design ensures comprehensive coverage for your antenna technologies across multiple industries and use cases.",
      details: [
        "Phased array antenna systems",
        "MIMO antenna configurations",
        "Cellular, WLAN, and IoT antennas",
        "Satellite communications antennas",
        "Millimeter wave antenna designs",
        "Antenna miniaturization techniques",
      ],
      linkText: "Antenna Patent Analysis",
      linkPath:
        "https://news-insights.kthlaw.com/en-us/patents/patent-analysis/antenna-analysis",
      externalLink: true,
    },
    {
      id: "sep-patents",
      title: "Standards Essential Patents (SEPs)",
      icon: (
        <SecurityIcon sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }} />
      ),
      image: "seps.png",
      description:
        "Navigate the complex world of Standards Essential Patents (SEPs) with our expert guidance. We help technology companies evaluate, protect, and monetize innovations that are essential to implementing technical standards across various industries.",
      details: [
        "3GPP and IEEE standard patent evaluation",
        "FRAND licensing counsel",
        "SEP quality and validity assessments",
        "Strategic positioning in standard-setting activities",
        "SEP portfolio development and management",
        "Licensing and monetization strategies",
      ],
      linkText: "SEP News & Insights",
      linkPath: "/news-insights?category=sep",
    },
    {
      id: "high-tech-patents",
      title: "High-Tech Electrical & Computer Patents",
      icon: (
        <DesktopWindowsIcon
          sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }}
        />
      ),
      image: "high-tech-hardware.png",
      description:
        "Specialized patenting for a broad range of technology innovations. We position your patents strategically in the fast-paced tech market to secure both protection and competitive advantage for your core technologies.",
      details: [
        "Embedded communication and control systems",
        "Computer hardware, memory, and interconnects",
        "Integrated circuits and microchip components",
        "Semiconductor devices and manufacturing processes",
        "Consumer and industrial networking equipment",
        "Medical devices and health technology",
      ],
      linkText: "High-Tech Patent Analysis",
      linkPath:
        "https://news-insights.kthlaw.com/en-us/patents/patent-analysis",
      externalLink: true,
    },
  ];

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <MemoryIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          Patent Specializations
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="body1" paragraph>
          Our law firm specializes in patent drafting, prosecution, analysis,
          and monetization for a wide range of engineering and technology
          disciplines. Our attorneys combine deep technical knowledge with legal
          expertise to provide comprehensive patent services across these
          specialized technical areas.
        </Typography>

        {specializations.map((spec, index) => (
          <Box key={spec.id} sx={{ mt: 4 }} id={spec.id}>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ order: { xs: 2, md: index % 2 === 0 ? 2 : 1 } }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  {spec.icon}
                  <Typography variant="h6" fontWeight="bold" color={tertColor}>
                    {spec.title}
                  </Typography>
                </Box>

                <Typography variant="body1" paragraph>
                  {spec.description}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                    border: `1px solid ${tertColor}33`,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Technologies We Protect:
                  </Typography>
                  <Grid container spacing={1}>
                    {spec.details.map((detail, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box display="flex" alignItems="flex-start">
                          <ChevronRightIcon
                            sx={{
                              color: tertColor,
                              mt: 0.5,
                              mr: 0.5,
                              fontSize: "1rem",
                            }}
                          />
                          <Typography variant="body2">{detail}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                <Button
                  component={spec.externalLink ? "a" : RouterLink}
                  to={!spec.externalLink ? spec.linkPath : undefined}
                  href={spec.externalLink ? spec.linkPath : undefined}
                  target={spec.externalLink ? "_blank" : undefined}
                  rel={spec.externalLink ? "noopener noreferrer" : undefined}
                  variant="contained"
                  sx={{
                    mt: 2,
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                  endIcon={
                    spec.externalLink ? <LaunchIcon /> : <ArrowForwardIosIcon />
                  }
                >
                  {spec.linkText}
                </Button>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ order: { xs: 1, md: index % 2 === 0 ? 1 : 2 } }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <img
                    src={getImageUrl(spec.image)}
                    alt={spec.title}
                    style={{
                      width: "100%",
                      maxWidth: "450px",
                      height: "auto",
                      borderRadius: "8px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Need a Patent Attorney for Your Engineering or Technology
            Innovation?
          </Typography>
          <Typography variant="body1" paragraph>
            Our specialized technical expertise across these domains allows us
            to understand your innovations at a deeper level, resulting in
            stronger patent applications and more effective strategic guidance.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: quadColor,
              color: "white",
              "&:hover": {
                backgroundColor: quadHoverColor,
              },
              mt: 1,
            }}
            onClick={() => {
              const contactSection = document.getElementById("contact-section");
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Schedule a Consultation
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KthPatentSpecializations;
